import { API_BASE_URL as API_BASE } from '../config/api';

export type RegisterPayload = {
  email: string;
  username: string;
  full_name?: string;
  phone?: string;
  password: string;
  account_type?: string;
};

export type TokenResponse = {
  access_token: string;
  token_type: string;
};

import { API_BASE_URL as API_BASE } from '../config/api';

export type RegisterPendingResponse = {
  message: string;
  email: string;
  requires_verification: boolean;
  otp_sent: boolean;
};

export type UserResponse = {
  id: number;
  email: string;
  username: string;
  full_name?: string | null;
  phone?: string | null;
  avatar?: string | null;
  banner?: string | null;
  is_active: boolean;
  is_superuser: boolean;
  account_type?: string | null; // tenant, landlord, business
  available_account_types?: string[] | null; // Danh sách các account types mà user đã có
  created_at: string;
  updated_at: string;
};

export type ForgotPasswordPayload = {
  email: string;
};

export type ResetPasswordPayload = {
  token: string;
  new_password: string;
};

export type ForgotPasswordResponse = {
  message: string;
};

export type SendEmailOtpResponse = {
  message: string;
  email_sent?: boolean;
  already_verified?: boolean;
  already_sent?: boolean;
  cooldown_remaining?: number;
};

export type VerifyEmailOtpResponse = {
  message: string;
  email_verified: boolean;
  user_id: number;
  account_type?: string;
  access_token: string;
  token_type: string;
};

export type ResetPasswordResponse = {
  message: string;
};

export type ChangePasswordPayload = {
  current_password: string;
  new_password: string;
};

export type ChangePasswordByCccdPayload = {
  cccd: string;
  new_password: string;
};

export type ChangePasswordResponse = {
  message: string;
};

export function getToken(): string | null {
  return localStorage.getItem('auth_token');
}

export function setToken(token: string) {
  localStorage.setItem('auth_token', token);
}

export function clearToken() {
  localStorage.removeItem('auth_token');
}

// NOTE: Token validation removed - Backend validates every request
// If token is invalid/expired, API will return 401 and frontend will logout
// See: AuthContext.tsx handleAuthError() for 401 handling

export async function login(email: string, password: string): Promise<TokenResponse> {
  const body = new URLSearchParams();
  body.set('username', email);
  body.set('password', password);

  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body,
  });
  if (!res.ok) {
    let detail = 'Login failed';
    try {
      const data = await res.json();
      detail = data.detail || data.message || detail;
    } catch {
      detail = (await res.text()) || detail;
    }
    throw new Error(typeof detail === 'string' ? detail : 'Login failed');
  }
  const data = (await res.json()) as TokenResponse;
  setToken(data.access_token);
  return data;
}

export async function register(payload: RegisterPayload): Promise<RegisterPendingResponse> {
  const res = await fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const contentType = res.headers.get('content-type') || '';

    // If server returns JSON (e.g. FastAPI / Pydantic errors)
    if (contentType.includes('application/json')) {
      const errorData = await res.json();

      // Handle Pydantic validation errors
      if (errorData.detail && Array.isArray(errorData.detail)) {
        const errorMessages = errorData.detail.map((err: any) => {
          if (err.loc && err.loc.length > 0) {
            const field = err.loc[err.loc.length - 1];
            const msg = err.msg || 'Validation error';
            return `${field === 'body' ? '' : field + ': '}${msg}`;
          }
          return err.msg || 'Validation error';
        });
        throw new Error(errorMessages.join('\n'));
      }

      // Handle simple error message
      if (errorData.detail && typeof errorData.detail === 'string') {
        throw new Error(errorData.detail);
      }

      throw new Error(errorData.message || 'Đăng ký thất bại');
    }

    // Non‑JSON response (plain text / HTML, etc.)
    const textError = await res.text();
    throw new Error(textError || 'Đăng ký thất bại. Vui lòng kiểm tra lại thông tin.');
  }
  const data = (await res.json()) as RegisterPendingResponse;
  return data;
}

export async function me(): Promise<UserResponse> {
  const token = getToken();
  if (!token) throw new Error('No token');
  
  console.log('Making request to:', `${API_BASE}/auth/me`);
  console.log('Using token:', token.substring(0, 20) + '...');
  
  const res = await fetch(`${API_BASE}/auth/me`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  
  console.log('Response status:', res.status);
  console.log('Response headers:', Object.fromEntries(res.headers.entries()));
  
  if (!res.ok) {
    const detail = await res.text();
    console.error('API Error:', {
      status: res.status,
      statusText: res.statusText,
      detail: detail
    });
    throw new Error(detail || 'Unauthorized');
  }
  const data = (await res.json()) as UserResponse;
  return data;
}

export async function sendEmailOtp(email: string, resend = false): Promise<SendEmailOtpResponse> {
  const res = await fetch(`${API_BASE}/auth/send-email-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, resend }),
  });
  if (!res.ok) {
    let detail = 'Không thể gửi mã xác minh';
    try {
      const data = await res.json();
      detail = data.detail || data.message || detail;
    } catch {
      detail = (await res.text()) || detail;
    }
    throw new Error(typeof detail === 'string' ? detail : 'Không thể gửi mã xác minh');
  }
  return (await res.json()) as SendEmailOtpResponse;
}

export async function verifyEmailOtp(email: string, otpCode: string): Promise<VerifyEmailOtpResponse> {
  const res = await fetch(`${API_BASE}/auth/verify-email-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otp_code: otpCode }),
  });
  if (!res.ok) {
    let detail = 'Mã xác minh không hợp lệ';
    try {
      const data = await res.json();
      detail = data.detail || data.message || detail;
    } catch {
      detail = (await res.text()) || detail;
    }
    throw new Error(typeof detail === 'string' ? detail : 'Mã xác minh không hợp lệ');
  }
  return (await res.json()) as VerifyEmailOtpResponse;
}

export async function forgotPassword(payload: ForgotPasswordPayload): Promise<ForgotPasswordResponse> {
  const res = await fetch(`${API_BASE}/auth/forgot-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(detail || 'Forgot password failed');
  }
  const data = (await res.json()) as ForgotPasswordResponse;
  return data;
}

export async function resetPassword(payload: ResetPasswordPayload): Promise<ResetPasswordResponse> {
  const res = await fetch(`${API_BASE}/auth/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(detail || 'Reset password failed');
  }
  const data = (await res.json()) as ResetPasswordResponse;
  return data;
}

export async function changePassword(payload: ChangePasswordPayload): Promise<ChangePasswordResponse> {
  const token = getToken();
  if (!token) throw new Error('No token');

  const res = await fetch(`${API_BASE}/auth/change-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const detail = await res.text();
    throw new Error(detail || 'Change password failed');
  }

  const data = (await res.json()) as ChangePasswordResponse;
  return data;
}

function parseApiErrorDetail(raw: string, fallback: string): string {
  try {
    const parsed = JSON.parse(raw) as { detail?: unknown };
    if (typeof parsed.detail === 'string') return parsed.detail;
    if (Array.isArray(parsed.detail)) {
      return parsed.detail
        .map((item) => (typeof item === 'object' && item && 'msg' in item ? String((item as { msg: string }).msg) : String(item)))
        .join(' | ');
    }
  } catch {
    // keep raw text
  }
  return raw || fallback;
}

export async function changePasswordByCccd(
  payload: ChangePasswordByCccdPayload,
): Promise<ChangePasswordResponse> {
  const token = getToken();
  if (!token) throw new Error('No token');

  const res = await fetch(`${API_BASE}/auth/change-password-cccd`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const detail = await res.text();
    throw new Error(parseApiErrorDetail(detail, 'Đổi mật khẩu thất bại'));
  }

  return (await res.json()) as ChangePasswordResponse;
}

export async function switchAccount(accountType: string): Promise<void> {
  const token = getToken();
  if (!token) throw new Error('No token');
  
  const res = await fetch(`${API_BASE}/accounts/switch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ new_account_type: accountType }),
  });
  
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(detail || 'Switch account failed');
  }
}
export async function googleLogin(googleToken: string): Promise<TokenResponse> {
  const res = await fetch(`${API_BASE}/auth/google-login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ google_token: googleToken }),
  });
  
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(detail || 'Google login failed');
  }
  
  const data = (await res.json()) as TokenResponse;
  setToken(data.access_token);
  return data;
}

// Load Google Sign-In SDK
export function loadGoogleSignIn(): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Sign-In SDK'));
    document.head.appendChild(script);
  });
}

// Initialize Google Sign-In
export function initializeGoogleSignIn(clientId: string, onSuccess: (idToken: string) => void): void {
  if (typeof window !== 'undefined' && (window as any).google) {
    (window as any).google.accounts.id.initialize({
      client_id: clientId,
      callback: (response: any) => {
        if (response.credential) {
          onSuccess(response.credential);
        }
      },
    });
  }
}

// Render Google Sign-In button (as fallback)
export function renderGoogleSignInButton(elementId: string, options?: any): void {
  if (typeof window !== 'undefined' && (window as any).google) {
    (window as any).google.accounts.id.renderButton(
      document.getElementById(elementId),
      options || { theme: 'outline', size: 'large' }
    );
  }
}
